import { BaseApiService } from '@doccareplus/api-client';

export interface AddressDto {
  street?: string | null;
  city: string;
  stateCode: string;
  stateName?: string | null;
  countryCode: string;
  countryName?: string | null;
  postalCode?: string | null;
}

export interface ClinicProfile {
  id: string;
  name: string;
  logoUrl?: string | null;
  address?: AddressDto | null;
  phone?: string | null;
  email?: string | null;
  timezone: string;
  currency: string;
  registrationNumber?: string | null;
  plan: string;
  status: string;
  onboardingCompletedAt?: string | null;
  isOnboardingComplete: boolean;
  type?: 'Allopathy' | 'Homeopathy' | 'Ayurveda' | 'Dental' | 'Physiotherapy' | null;
}

export interface UpdateClinicProfileRequest {
  name: string;
  timezone: string;
  currency: string;
  logoUrl?: string | null;
  phone?: string | null;
  email?: string | null;
  registrationNumber?: string | null;
  street?: string | null;
  city?: string | null;
  stateCode?: string | null;
  country?: string | null;
  postalCode?: string | null;
}

import type { PrintMode } from '@/shared/prescription';

export type { PrintMode };

/**
 * The clinic's bounded prescription-format overrides, layered on top of its system-of-medicine preset. Keys are the
 * section/slot enum names ("Diagnosis", "Dose"); the backend rejects anything it does not recognise rather than
 * ignoring it. Mirrors Contracts.Settings.Common.PrescriptionFormatOverrides{Request,Response}.
 *
 * These are the clinic's *deltas* only — the resolved format (preset + deltas) comes from the prescription-layout
 * endpoint, which is what the live preview renders.
 */
export interface PrescriptionFormatOverrides {
  hiddenSections: string[];
  sectionLabels: Record<string, string>;
  hiddenSlots: string[];
  slotLabels: Record<string, string>;
  slotVocabularies: Record<string, string[]>;
  rxHeading?: string | null;
}

export interface ClinicSettings {
  defaultPrintMode: PrintMode;
  pageSize?: import('@/shared/prescription').PrescriptionPageSize;
  customPageWidthMm?: number | null;
  customPageHeightMm?: number | null;
  topMarginMm: number;
  bottomMarginMm: number;
  leftMarginMm: number;
  rightMarginMm: number;
  showLogo: boolean;
  showClinicName: boolean;
  showRegistrationNumber: boolean;
  showAddress: boolean;
  showPhone: boolean;
  showEmail: boolean;
  headerSubtitle?: string | null;
  footerLines: string[];
  showPageNumber: boolean;
  showDigitallySignedNotice: boolean;
  accentColor: string;
  smsRemindersEnabled: boolean;
  whatsAppRemindersEnabled: boolean;
  emailRemindersEnabled: boolean;
  reminderOffsetsMinutes: number[];
  requireTwoFactorForStaff: boolean;
  smartQueueEnabled: boolean;
  /** Advisory per-provider, per-30-min-bracket overload warning threshold in the booking drawer. 0 disables it. */
  maxAppointmentsPerBracket: number;
  /** Explicit opt-in to arbitrary staff-entered dates, times and durations. */
  allowFreeFormScheduling: boolean;
  prescriptionFormat: PrescriptionFormatOverrides;
  intakeForm: IntakeFormOverrides;
}

export type IntakeFieldKey = 'Weight' | 'BloodPressure' | 'Temperature' | 'Pulse';
export interface IntakeFormOverrides {
  fieldOrder: IntakeFieldKey[];
  hiddenFields: IntakeFieldKey[];
  requiredFields: IntakeFieldKey[];
  weightUnit?: 'kg' | 'lb' | null;
  temperatureUnit?: 'C' | 'F' | null;
}
export interface IntakeFormResponse {
  clinicType: NonNullable<ClinicProfile['type']>;
  fields: Array<{ key: IntakeFieldKey; labelKey: string; required: boolean }>;
  weightUnit: 'kg' | 'lb';
  temperatureUnit: 'C' | 'F';
}

export interface ServiceItem {
  id: string;
  name: string;
  defaultPrice: number;
  defaultDurationMinutes?: number | null;
  isActive: boolean;
}

export interface RoomItem { id: string; name: string; isActive: boolean }
export interface TimeInterval { start: string; end: string }
export interface DayAvailability { day: string; intervals: TimeInterval[] }
export interface ProviderAvailability { providerUserId: string; providerName?: string | null; days: DayAvailability[] }
export interface Holiday { id: string; date: string; name: string; isActive: boolean }
export interface Leave {
  id: string; providerUserId: string; providerName?: string | null; startDate: string; endDate: string;
  startTime?: string | null; endTime?: string | null; reason: string; status: string;
}
export interface Availability { clinicDays: DayAvailability[]; providers: ProviderAvailability[]; holidays: Holiday[]; leaves: Leave[] }

export interface AccountSettings {
  theme: 'System' | 'Light' | 'Dark';
  language: 'en';
  notificationSoundsEnabled: boolean;
  inactivityTimeoutMinutes: 15 | 30 | 60 | 120;
  twoFactorEnabled: boolean;
  phoneNumber?: string | null;
}

export interface SessionItem {
  id: string; deviceName: string; approximateLocation?: string | null;
  createdAt: string; lastActivityAt: string; expiresAt: string;
}

class ClinicSettingsApi extends BaseApiService {
  constructor() { super('/clinic'); }
  getClinicProfile() { return this.get<ClinicProfile>('/profile'); }
  updateClinicProfile(body: UpdateClinicProfileRequest) { return this.put<ClinicProfile, UpdateClinicProfileRequest>('/profile', body); }
  getClinicSettings() { return this.get<ClinicSettings>('/settings'); }
  getIntakeForm() { return this.get<IntakeFormResponse>('/visits/intake-form'); }
  updateClinicSettings(body: ClinicSettings) { return this.put<ClinicSettings, ClinicSettings>('/settings', body); }
  getServices() { return this.get<ServiceItem[]>('/services'); }
  async addService(body: Omit<ServiceItem, 'id' | 'isActive'>) {
    const response = await this.post<ServiceItem[], { services: Array<Omit<ServiceItem, 'id' | 'isActive'>> }>('/services', { services: [body] });
    return response[0];
  }
  updateService(body: ServiceItem) { return this.put<ServiceItem, Omit<ServiceItem, 'id'>>(`/services/${body.id}`, body); }
  getRooms() { return this.get<RoomItem[]>('/rooms'); }
  async addRoom(name: string) {
    const response = await this.post<RoomItem[], { rooms: Array<{ name: string }> }>('/rooms', { rooms: [{ name }] });
    return response[0];
  }
  updateRoom(body: RoomItem) { return this.put<RoomItem, Omit<RoomItem, 'id'>>(`/rooms/${body.id}`, body); }
  getAvailability() { return this.get<Availability>('/availability'); }
  replaceAvailability(body: Pick<Availability, 'clinicDays' | 'providers' | 'holidays'>) { return this.put<Availability, typeof body>('/availability', body); }
  createLeave(body: { startDate: string; endDate: string; startTime?: string | null; endTime?: string | null; reason: string }) { return this.post<Leave, typeof body>('/availability/leaves', body); }
  decideLeave(leaveId: string, decision: 'Approved' | 'Rejected') { return this.put<Leave, { decision: string }>(`/availability/leaves/${leaveId}/decision`, { decision }); }
  cancelLeave(leaveId: string) { return this.post<Leave>(`/availability/leaves/${leaveId}/cancel`); }
}

class AccountApi extends BaseApiService {
  constructor() { super('/account'); }
  getAccountSettings() { return this.get<AccountSettings>('/settings'); }
  updateAccountSettings(body: Omit<AccountSettings, 'twoFactorEnabled'>) { return this.put<AccountSettings, typeof body>('/settings', body); }
  getSessions() { return this.get<SessionItem[]>('/settings/sessions'); }
  revokeSession(sessionId: string) { return this.delete<boolean>(`/settings/sessions/${sessionId}`); }
  setTwoFactor(enabled: boolean, otpCode?: string) { return this.put<AccountSettings, { enabled: boolean; otpCode?: string }>('/settings/two-factor', { enabled, otpCode }); }
}

const clinicApi = new ClinicSettingsApi();
const accountApi = new AccountApi();

export const settingsApi = {
  getClinicProfile: () => clinicApi.getClinicProfile(),
  updateClinicProfile: (body: UpdateClinicProfileRequest) => clinicApi.updateClinicProfile(body),
  getClinicSettings: () => clinicApi.getClinicSettings(),
  getIntakeForm: () => clinicApi.getIntakeForm(),
  updateClinicSettings: (body: ClinicSettings) => clinicApi.updateClinicSettings(body),
  getServices: () => clinicApi.getServices(),
  addService: (body: Omit<ServiceItem, 'id' | 'isActive'>) => clinicApi.addService(body),
  updateService: (body: ServiceItem) => clinicApi.updateService(body),
  getRooms: () => clinicApi.getRooms(),
  addRoom: (name: string) => clinicApi.addRoom(name),
  updateRoom: (body: RoomItem) => clinicApi.updateRoom(body),
  getAvailability: () => clinicApi.getAvailability(),
  replaceAvailability: (body: Pick<Availability, 'clinicDays' | 'providers' | 'holidays'>) => clinicApi.replaceAvailability(body),
  createLeave: (body: { startDate: string; endDate: string; startTime?: string | null; endTime?: string | null; reason: string }) => clinicApi.createLeave(body),
  decideLeave: (input: { leaveId: string; decision: 'Approved' | 'Rejected' }) => clinicApi.decideLeave(input.leaveId, input.decision),
  cancelLeave: (leaveId: string) => clinicApi.cancelLeave(leaveId),
  getAccountSettings: () => accountApi.getAccountSettings(),
  updateAccountSettings: (body: Omit<AccountSettings, 'twoFactorEnabled'>) => accountApi.updateAccountSettings(body),
  getSessions: () => accountApi.getSessions(),
  revokeSession: (sessionId: string) => accountApi.revokeSession(sessionId),
  setTwoFactor: (enabled: boolean, otpCode?: string) => accountApi.setTwoFactor(enabled, otpCode),
};
